import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { get, post } from "../services/apiClient";

export type PlanChangeRequestStatus =
  | "PENDING"
  | "APPROVED"
  | "REJECTED"
  | "CANCELLED";

export interface PlanRef {
  id: string;
  code: string;
  name: string;
  priceMonthly: number | string;
}

export interface PlanChangeRequestSummary {
  id: string;
  ownerId: string;
  currentPlanId: string | null;
  targetPlanId: string;
  status: PlanChangeRequestStatus;
  rejectionReason?: string | null;
  note?: string | null;
  reviewedAt?: string | null;
  createdAt: string;
  updatedAt: string;
  targetPlan: PlanRef;
  currentPlan: PlanRef | null;
  reviewer?: { id: string; name: string; email: string } | null;
  owner: {
    id: string;
    name: string | null;
    phone: string | null;
    email: string | null;
    subscriptions: Array<{
      status: string;
      currentPeriodEnd: string | null;
      plan: { code: string; name: string };
    }>;
  };
}

export interface PlanChangeRequestListResponse {
  data: PlanChangeRequestSummary[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export const planChangeRequestKeys = {
  all: ["plan-change-requests"] as const,
  list: (params: {
    status?: PlanChangeRequestStatus;
    page: number;
    limit: number;
  }) => [...planChangeRequestKeys.all, "list", params] as const,
};

export const usePlanChangeRequestList = (params: {
  status?: PlanChangeRequestStatus;
  page?: number;
  limit?: number;
}) => {
  const page = params.page ?? 1;
  const limit = params.limit ?? 25;
  return useQuery<PlanChangeRequestListResponse>({
    queryKey: planChangeRequestKeys.list({
      status: params.status,
      page,
      limit,
    }),
    queryFn: () =>
      get<PlanChangeRequestListResponse>("/admin/plan-change-requests", {
        ...(params.status ? { status: params.status } : {}),
        page,
        limit,
      }),
  });
};

export const useApprovePlanChangeRequest = () => {
  const qc = useQueryClient();
  return useMutation<unknown, Error, { id: string; note?: string }>({
    mutationFn: ({ id, note }) =>
      post(`/admin/plan-change-requests/${id}/approve`, { note }),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: planChangeRequestKeys.all }),
  });
};

export const useRejectPlanChangeRequest = () => {
  const qc = useQueryClient();
  return useMutation<
    unknown,
    Error,
    { id: string; rejectionReason: string; note?: string }
  >({
    mutationFn: ({ id, rejectionReason, note }) =>
      post(`/admin/plan-change-requests/${id}/reject`, {
        rejectionReason,
        note,
      }),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: planChangeRequestKeys.all }),
  });
};
